// Cache to store already loaded metadata fields
const metadataCache = {};

/**
 * Dynamically import and get metadata field keys for a specific behavior
 * @param {string} behaviorAcronym - The behavior acronym (e.g., 'ESFOC', 'OT', 'ET')
 * @returns {Promise<string[]>} Promise resolving to array of metadata field keys
 */
export const getBehaviourMetadataFields = async (behaviorAcronym) => {
  const upperAcronym = behaviorAcronym?.toUpperCase();

  if (!upperAcronym) return [];

  // Return cached value if available
  if (metadataCache[upperAcronym]) {
    return metadataCache[upperAcronym];
  }

  try {
    // Dynamically import the behavior graph module
    const module = await import(`./BehaviourGraphs/${upperAcronym}.jsx`);

    // Extract metadata fields if exported
    const fields = module.behaviourMetadataFields || [];

    // Cache the result
    metadataCache[upperAcronym] = fields;

    return fields;
  } catch (error) {
    console.warn(
      `Could not load metadata fields for behavior: ${upperAcronym}`,
      error,
    );
    return [];
  }
};

/**
 * Synchronous version - returns cached metadata fields or empty array
 * Use this if you've already called the async version at least once
 * @param {string} behaviorAcronym - The behavior acronym
 * @returns {string[]} Array of metadata field keys
 */
export const getBehaviourMetadataFieldsSync = (behaviorAcronym) => {
  const upperAcronym = behaviorAcronym?.toUpperCase();
  return metadataCache[upperAcronym] || [];
};

/**
 * Preload metadata fields for multiple behaviors at once
 * @param {string[]} acronyms - Array of behavior acronyms to preload
 * @returns {Promise<void>}
 */
export const preloadBehaviourMetadataFields = async (acronyms) => {
  await Promise.all(
    acronyms.map((acronym) => getBehaviourMetadataFields(acronym)),
  );
};

export default {
  getBehaviourMetadataFields,
  getBehaviourMetadataFieldsSync,
  preloadBehaviourMetadataFields,
};
